import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import events from '../data/events';

const Calendar = () => {
  const [currentView, setCurrentView] = useState('month');
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [selectedFilters, setSelectedFilters] = useState({
    holiday: true,
    academic: true,
    earlyDismissal: true,
    testing: true,
    pta: true,
    afterSchool: true
  });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [language, setLanguage] = useState('en');
  const [currentWeekOffset, setCurrentWeekOffset] = useState(0);
  
  // events are imported from shared data module
  
  // Generate days for the calendar
  const getDaysInMonth = (month, year) => {
    const date = new Date(year, month, 1);
    const days = [];
    
    // Get the first day of the month
    const firstDay = new Date(year, month, 1).getDay();
    
    // Add empty slots for days from previous month
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    
    // Add days of current month
    while (date.getMonth() === month) {
      days.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    
    return days;
  };

  // Helpers for Week view
  const getStartOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    d.setDate(d.getDate() - day); // Sunday as start
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const getWeekDays = () => {
    const base = new Date(currentYear, currentMonth, 1);
    const start = getStartOfWeek(base);
    const weekStart = new Date(start);
    weekStart.setDate(start.getDate() + currentWeekOffset * 7);
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(weekStart);
      d.setDate(weekStart.getDate() + i);
      days.push(d);
    }
    return { weekStart, days };
  };
  
  // Function to filter events
  const getFilteredEvents = () => {
    return events.filter(event => {
      const category = event.category;
      return selectedFilters[category];
    });
  };
  
  // Get upcoming events (next 5 events from today)
  const getUpcomingEvents = () => {
    const today = new Date();
    return getFilteredEvents()
      .filter(event => event.date >= today)
      .sort((a, b) => a.date - b.date)
      .slice(0, 5);
  };
  
  // Function to navigate to previous month (or week in week view)
  const prevMonth = () => {
    if (currentView === 'week') {
      setCurrentWeekOffset(currentWeekOffset - 1);
      return;
    }
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };
  
  // Function to navigate to next month (or week in week view)
  const nextMonth = () => {
    if (currentView === 'week') {
      setCurrentWeekOffset(currentWeekOffset + 1);
      return;
    }
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };
  
  // Color mapping for categories - updated with burgundy theme
  const categoryColors = {
    holiday: 'bg-[#630503]',
    academic: 'bg-amber-700',
    earlyDismissal: 'bg-orange-600',
    testing: 'bg-purple-700',
    pta: 'bg-yellow-600',
    afterSchool: 'bg-green-700'
  };
  
  // Get readable category names
  const getCategoryName = (category) => {
    switch(category) {
      case 'holiday': return 'Holiday';
      case 'academic': return 'Academic';
      case 'earlyDismissal': return 'Early Dismissal';
      case 'testing': return 'Testing';
      case 'pta': return 'PTA';
      case 'afterSchool': return 'After-School';
      default: return category;
    }
  };

  const getCategoryNameES = (category) => {
    switch(category) {
      case 'holiday': return 'Feriado';
      case 'academic': return 'Académico';
      case 'earlyDismissal': return 'Salida Temprana';
      case 'testing': return 'Exámenes';
      case 'pta': return 'PTA';
      case 'afterSchool': return 'Programas Después de Clases';
      default: return category;
    }
  };
  
  // Format date for display
  const formatDate = (dateString) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', options);
  };

  const getEventTitle = (event) => {
    return language === 'es' && event.titleES ? event.titleES : event.title;
  };

  const getEventDescription = (event) => {
    if (language === 'es' && event.descriptionES) return event.descriptionES;
    return event.description || '';
  };
  
  // Handle filter change
  const toggleFilter = (filter) => {
    setSelectedFilters({
      ...selectedFilters,
      [filter]: !selectedFilters[filter]
    });
  };
  
  // Get events for a specific day
  const getEventsForDay = (day) => {
    return events.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.getDate() === day.getDate() &&
             eventDate.getMonth() === day.getMonth() &&
             eventDate.getFullYear() === day.getFullYear() &&
             (selectedFilters[event.category] || false);
    });
  };
  
  // Month names for display
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  

  
  const days = getDaysInMonth(currentMonth, currentYear);
  const upcomingEvents = getUpcomingEvents();
  
  const closeModal = () => {
    setSelectedEvent(null);
  };
  
  return (
    <main id="main-content" className="bg-white">
      <Navbar />
      
      {/* Header Section */}
      <section className="pt-32 pb-24 bg-[#630503] relative overflow-hidden">
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-1/5 bg-gradient-to-b from-[#630503] to-white"></div>

        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            {/* Header Section */}
            <div className="mb-16">
              <span className="inline-block bg-white/10 backdrop-blur-sm text-white px-6 py-3 text-sm font-medium uppercase tracking-[0.2em] mb-8 rounded-full">
                School Events
              </span>
              <h1 className="text-4xl lg:text-5xl xl:text-6xl font-light text-white mb-8 leading-[1.1] max-w-4xl mx-auto">
                School <span className="font-bold">Calendar</span>
              </h1>
              <div className="w-24 h-px bg-white/40 mx-auto mb-8"></div>
              <p className="text-lg lg:text-xl leading-relaxed text-white/95 max-w-3xl mx-auto">
                Stay informed about school events, holidays, and important dates throughout the academic year.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Main Calendar Section */}
      <section id="monthly-calendar" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <span className="inline-block bg-[#630503] text-white px-6 py-3 text-sm font-bold uppercase tracking-wider mb-8 shadow-lg">
              Interactive Calendar
            </span>
            <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 mb-8 leading-tight">Monthly Calendar</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              View school events, filter by category, and stay up-to-date with important dates and activities.
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Calendar Filters / Sidebar */}
            <div className="lg:col-span-1 bg-white rounded-xl shadow-lg border border-gray-100 p-8 hover:shadow-xl transition-shadow duration-300">
              <h3 className="text-2xl font-bold text-[#630503] mb-8">Filter Events</h3>
              
              <div className="space-y-3">
                <div>
                  <div className="flex items-center">
                    <input 
                      id="filter-holiday"
                      type="checkbox" 
                      checked={selectedFilters.holiday}
                      onChange={() => toggleFilter('holiday')}
                      className="h-5 w-5 text-[#630503] rounded border-gray-300 focus:ring-[#630503] focus:ring-offset-2"
                    />
                    <label htmlFor="filter-holiday" className="ml-2 flex items-center text-gray-700">
                      <span className="h-3 w-3 bg-[#630503] rounded-full mr-2"></span>
                      Holidays
                    </label>
                  </div>
                </div>
                
                <div>
                  <div className="flex items-center">
                    <input 
                      id="filter-academic"
                      type="checkbox" 
                      checked={selectedFilters.academic}
                      onChange={() => toggleFilter('academic')}
                      className="h-5 w-5 text-amber-700 rounded border-gray-300 focus:ring-amber-700 focus:ring-offset-2"
                    />
                    <label htmlFor="filter-academic" className="ml-2 flex items-center text-gray-700">
                      <span className="h-3 w-3 bg-amber-700 rounded-full mr-2"></span>
                      Academic Events
                    </label>
                  </div>
                </div>
                
                <div>
                  <div className="flex items-center">
                    <input 
                      id="filter-earlyDismissal"
                      type="checkbox" 
                      checked={selectedFilters.earlyDismissal}
                      onChange={() => toggleFilter('earlyDismissal')}
                      className="h-5 w-5 text-orange-600 rounded border-gray-300 focus:ring-orange-600 focus:ring-offset-2"
                    />
                    <label htmlFor="filter-earlyDismissal" className="ml-2 flex items-center text-gray-700">
                      <span className="h-3 w-3 bg-orange-600 rounded-full mr-2"></span>
                      Early Dismissal
                    </label>
                  </div>
                </div>
                
                <div>
                  <div className="flex items-center">
                    <input 
                      id="filter-testing"
                      type="checkbox" 
                      checked={selectedFilters.testing}
                      onChange={() => toggleFilter('testing')}
                      className="h-5 w-5 text-purple-700 rounded border-gray-300 focus:ring-purple-700 focus:ring-offset-2"
                    />
                    <label htmlFor="filter-testing" className="ml-2 flex items-center text-gray-700">
                      <span className="h-3 w-3 bg-purple-700 rounded-full mr-2"></span>
                      Testing
                    </label>
                  </div>
                </div>
                
                <div>
                  <div className="flex items-center">
                    <input 
                      id="filter-pta"
                      type="checkbox" 
                      checked={selectedFilters.pta}
                      onChange={() => toggleFilter('pta')}
                      className="h-5 w-5 text-yellow-600 rounded border-gray-300 focus:ring-yellow-600 focus:ring-offset-2"
                    />
                    <label htmlFor="filter-pta" className="ml-2 flex items-center text-gray-700">
                      <span className="h-3 w-3 bg-yellow-600 rounded-full mr-2"></span>
                      PTA Events
                    </label>
                  </div>
                </div>
                
                <div>
                  <div className="flex items-center">
                    <input 
                      id="filter-afterSchool"
                      type="checkbox" 
                      checked={selectedFilters.afterSchool}
                      onChange={() => toggleFilter('afterSchool')}
                      className="h-5 w-5 text-green-700 rounded border-gray-300 focus:ring-green-700 focus:ring-offset-2"
                    />
                    <label htmlFor="filter-afterSchool" className="ml-2 flex items-center text-gray-700">
                      <span className="h-3 w-3 bg-green-700 rounded-full mr-2"></span>
                      After-School Programs
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="mt-10">
                <h3 className="text-xl font-bold text-[#630503] mb-6">Language</h3>
                <div className="flex space-x-2 mb-6">
                  <button
                    onClick={() => setLanguage('en')}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#630503] ${
                      language === 'en' ? 'bg-[#630503] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    aria-pressed={language === 'en'}
                  >
                    EN
                  </button>
                  <button
                    onClick={() => setLanguage('es')}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#630503] ${
                      language === 'es' ? 'bg-[#630503] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                    aria-pressed={language === 'es'}
                  >
                    ES
                  </button>
                </div>
              </div>

              <div className="mt-10">
                <h3 className="text-xl font-bold text-[#630503] mb-6">View Options</h3>
                <div className="flex space-x-2 mb-6">
                  <button 
                    onClick={() => setCurrentView('month')}
                                          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#630503] ${
                        currentView === 'month' 
                          ? 'bg-[#630503] text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    aria-pressed={currentView === 'month'}
                  >
                    Month
                  </button>
                  <button 
                    onClick={() => { setCurrentView('week'); setCurrentWeekOffset(0); }}
                                          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#630503] ${
                        currentView === 'week' 
                          ? 'bg-[#630503] text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    aria-pressed={currentView === 'week'}
                  >
                    Week
                  </button>
                  <button 
                    onClick={() => setCurrentView('list')}
                                          className={`px-4 py-2 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#630503] ${
                        currentView === 'list' 
                          ? 'bg-[#630503] text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    aria-pressed={currentView === 'list'}
                  >
                    List
                  </button>
                </div>
                
                <button 
                  onClick={() => {
                    setCurrentMonth(new Date().getMonth());
                    setCurrentYear(new Date().getFullYear());
                    setCurrentWeekOffset(0);
                  }}
                  className="w-full py-3 border border-[#630503] text-[#630503] rounded-md hover:bg-[#630503] hover:text-white transition-colors focus:outline-none focus:ring-2 focus:ring-[#630503] focus:ring-offset-2 font-semibold"
                >
                  Today
                </button>
              </div>
              
              <div className="mt-10">
                <h3 className="text-xl font-bold text-[#630503] mb-6">Upcoming Events</h3>
                <div className="space-y-4">
                  {upcomingEvents.length > 0 ? (
                    upcomingEvents.map((event) => (
                      <div key={event.id} className="border-l-4 pl-4 py-2" style={{
                        borderColor: event.category === 'holiday' ? '#630503' : 
                                    event.category === 'academic' ? '#d97706' : 
                                    event.category === 'earlyDismissal' ? '#f97316' : 
                                    event.category === 'testing' ? '#9333ea' : 
                                    event.category === 'pta' ? '#eab308' : '#16a34a'
                      }}>
                        <div className="text-sm text-gray-600">{formatDate(event.date)}</div>
                        <div className="font-medium text-gray-900">{getEventTitle(event)}</div>
                        <div className="text-sm text-gray-700">
                          {event.allDay ? 'All Day' : `${event.startTime} - ${event.endTime}`}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 italic">No upcoming events based on your filters.</p>
                  )}
                </div>
              </div>
            </div>
            
            {/* Main Calendar Area */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow duration-300">
                {/* Calendar Header */}
                <div className="bg-[#630503] text-white px-8 py-6 flex items-center justify-between">
                  <div>
                    {currentView === 'week' ? (
                      (() => {
                        const { days } = getWeekDays();
                        const startLabel = `${days[0].toLocaleString('default', { month: 'short' })} ${days[0].getDate()}`;
                        const endLabel = `${days[6].toLocaleString('default', { month: 'short' })} ${days[6].getDate()}, ${days[6].getFullYear()}`;
                        return (
                          <h3 className="text-xl font-bold">Week of {startLabel} – {endLabel}</h3>
                        );
                      })()
                    ) : (
                      <h3 className="text-xl font-bold">{monthNames[currentMonth]} {currentYear}</h3>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button 
                      onClick={prevMonth}
                      className="p-2 rounded-full hover:bg-[#4a0402] transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#630503]"
                      aria-label="Previous month"
                    >
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button 
                      onClick={nextMonth}
                      className="p-2 rounded-full hover:bg-[#4a0402] transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#630503]"
                      aria-label="Next month"
                    >
                      <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
                
                {/* Weekday Headers */}
                <div className="grid grid-cols-7 text-center bg-gray-50 border-b border-gray-200">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => (
                    <div key={i} className="py-2 font-medium text-gray-700">
                      {day}
                    </div>
                  ))}
                </div>
                
                {/* Calendar Grid - Month View */}
                {currentView === 'month' && (
                  <div className="grid grid-cols-7 auto-rows-fr border-b border-gray-200 min-h-[600px]">
                    {days.map((day, index) => {
                      const dayEvents = day ? getEventsForDay(day) : [];
                      const isToday = day && day.toDateString() === new Date().toDateString();
                      
                      return (
                        <div 
                          key={index} 
                          className={`border-t border-l p-2 h-32 ${
                            day ? 'bg-white' : 'bg-gray-50 text-gray-600'
                          } ${
                            isToday ? 'bg-[#630503]/5 ring-2 ring-inset ring-[#630503]' : ''
                          } ${
                            index % 7 === 6 ? 'border-r' : ''
                          }`}
                        >
                          {day && (
                            <>
                              <div className={`text-right mb-1 ${isToday ? 'font-bold text-[#630503]' : ''}`}>
                                {day.getDate()}
                              </div>
                              <div className="space-y-1 overflow-y-auto max-h-[80px]">
                                {dayEvents.map((event) => (
                                  <button 
                                    key={event.id}
                                    onClick={() => {
                                      setSelectedEvent(event);
                                    }}
                                    className={`text-xs w-full text-left px-2 py-1 rounded text-white ${categoryColors[event.category]} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#630503] flex items-center justify-between`}
                                    title={getEventTitle(event)}
                                    aria-label={`${getEventTitle(event)} on ${formatDate(event.date)}`}
                                  >
                                    <span className="truncate">{getEventTitle(event)}</span>
                                    {event.image && (
                                      <span className="ml-2 shrink-0 inline-flex items-center justify-center bg-white/90 rounded p-0.5">
                                        <img src={event.image} alt={event.imageAlt || 'QR'} className="w-4 h-4 object-contain" />
                                      </span>
                                    )}
                                  </button>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Week View */}
                {currentView === 'week' && (
                  (() => {
                    const { days: weekDays } = getWeekDays();
                    return (
                      <div className="border-b border-gray-200 min-h-[400px]">
                        <div className="grid grid-cols-7 text-center bg-gray-50 border-b border-gray-200">
                          {weekDays.map((d, i) => (
                            <div key={i} className="py-2 font-medium text-gray-700">
                              <div>{['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.getDay()]}</div>
                              <div className="text-xs text-gray-500">{d.toLocaleString('default', { month: 'short' })} {d.getDate()}</div>
                            </div>
                          ))}
                        </div>
                        <div className="grid grid-cols-7 auto-rows-fr min-h-[320px]">
                          {weekDays.map((day, index) => {
                            const dayEvents = getEventsForDay(day);
                            const isToday = day.toDateString() === new Date().toDateString();
                            return (
                              <div key={index} className={`border-t border-l p-3 ${index % 7 === 6 ? 'border-r' : ''} ${isToday ? 'bg-[#630503]/5 ring-2 ring-inset ring-[#630503]' : 'bg-white'}`}>
                                <div className={`text-right mb-2 ${isToday ? 'font-bold text-[#630503]' : ''}`}>{day.getDate()}</div>
                                <div className="space-y-2 overflow-y-auto max-h-[260px]">
                                  {dayEvents.length === 0 && (
                                    <div className="text-xs text-gray-600 italic">{language === 'es' ? 'Sin eventos' : 'No events'}</div>
                                  )}
                                  {dayEvents.map((event) => (
                                    <button
                                      key={event.id}
                                      onClick={() => setSelectedEvent(event)}
                                      className={`text-xs w-full text-left px-2 py-1 rounded text-white ${categoryColors[event.category]} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#630503] flex items-center justify-between`}
                                      title={getEventTitle(event)}
                                      aria-label={`${getEventTitle(event)} on ${formatDate(event.date)}`}
                                    >
                                      <span className="truncate">{getEventTitle(event)}</span>
                                      {event.image && (
                                        <span className="ml-2 shrink-0 inline-flex items-center justify-center bg-white/90 rounded p-0.5">
                                          <img src={event.image} alt={event.imageAlt || 'QR'} className="w-4 h-4 object-contain" />
                                        </span>
                                      )}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()
                )}
                
                {/* List View */}
                {currentView === 'list' && (
                  <div className="border-b border-gray-200 min-h-[600px] p-4">
                    <div className="space-y-4">
                      {getFilteredEvents()
                        .filter(event => 
                          event.date.getMonth() === currentMonth && 
                          event.date.getFullYear() === currentYear
                        )
                        .sort((a, b) => a.date - b.date)
                        .map(event => (
                          <div key={event.id} className="flex border-l-4 p-4 bg-gray-50 rounded-r" style={{
                            borderColor: event.category === 'holiday' ? '#630503' : 
                                      event.category === 'academic' ? '#d97706' : 
                                      event.category === 'earlyDismissal' ? '#f97316' : 
                                      event.category === 'testing' ? '#9333ea' : 
                                      event.category === 'pta' ? '#eab308' : '#16a34a'
                          }}>
                            <div className="mr-6 text-center">
                              <div className="font-bold text-lg">{event.date.getDate()}</div>
                              <div className="text-xs text-gray-600">
                                {event.date.toLocaleString('default', { month: 'short' })}
                              </div>
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{getEventTitle(event)}</div>
                              <div className="text-sm text-gray-700">
                                {event.allDay ? 'All Day' : `${event.startTime} - ${event.endTime}`}
                              </div>
                              <div className="mt-1 text-sm text-gray-600">{getEventDescription(event)}</div>
                              <div className="mt-2">
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  event.category === 'holiday' ? 'bg-[#630503]/10 text-[#630503]' : 
                                  event.category === 'academic' ? 'bg-amber-100 text-amber-900' : 
                                  event.category === 'earlyDismissal' ? 'bg-orange-100 text-orange-900' : 
                                  event.category === 'testing' ? 'bg-purple-100 text-purple-900' : 
                                  event.category === 'pta' ? 'bg-yellow-100 text-yellow-900' : 
                                  'bg-green-100 text-green-900'
                                }`}>
                                  {language === 'es' ? getCategoryNameES(event.category) : getCategoryName(event.category)}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      

      {/* Contact/Help Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#4a0402] rounded-xl shadow-xl p-10 md:p-12 text-white">
            <div className="md:flex md:items-center md:justify-between">
              <div className="max-w-xl">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Need help with the calendar?
                </h2>
                <p className="text-xl text-white/90 mb-6">
                  Contact our main office for questions about the calendar or school schedules.
                </p>
                <div className="space-y-2 text-white/80">
                  <p className="flex items-center">
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    Phone: (718) 555-1234
                  </p>
                  <p className="flex items-center">
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Email: calendar@ps011x.org
                  </p>
                </div>
              </div>
              <div className="mt-8 md:mt-0">
                <button
                  type="button"
                  className="inline-flex items-center px-8 py-4 border-2 border-white text-lg font-bold rounded-md text-white hover:bg-white hover:text-[#630503] transition-colors focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2"
                >
                  Contact Us
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Event Details Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 z-10 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div 
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
              aria-hidden="true"
              onClick={closeModal}
            ></div>
            
            {/* Modal panel */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div 
              className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-headline"
            >
              <div className={`${categoryColors[selectedEvent.category]} py-6 px-8`}>
                <h3 id="modal-headline" className="text-2xl font-bold text-white">
                  {getEventTitle(selectedEvent)}
                </h3>
                <p className="text-white/90 mt-2 text-lg">
                  {formatDate(selectedEvent.date)}
                </p>
              </div>
              
              <div className="bg-white px-8 py-6">
                <div className="text-gray-600">
                  <p className="mb-2 text-lg leading-relaxed">{getEventDescription(selectedEvent)}</p>
                  {(selectedEvent.description || selectedEvent.descriptionES) && (
                    <p className="mb-6 text-sm leading-relaxed text-gray-500 italic">
                      {language === 'es' ? selectedEvent.description : (selectedEvent.descriptionES || '')}
                    </p>
                  )}
                  
                  {(selectedEvent.time || selectedEvent.startTime || selectedEvent.endTime || selectedEvent.allDay) && (
                    <div className="flex items-center mb-3">
                      <svg className="h-5 w-5 text-gray-400 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                        <path fillRule="evenodd" d="M6.75 2.25A.75.75 0 017.5 3v1.5h9V3a.75.75 0 011.5 0v1.5h.75A2.25 2.25 0 0121 6.75v10.5A2.25 2.25 0 0118.75 19.5H5.25A2.25 2.25 0 013 17.25V6.75A2.25 2.25 0 015.25 4.5H6V3a.75.75 0 01.75-.75zM4.5 9h15v8.25a.75.75 0 01-.75.75H5.25a.75.75 0 01-.75-.75V9z" clipRule="evenodd" />
                      </svg>
                      <span>
                        {selectedEvent.allDay ? (language === 'es' ? 'Todo el día' : 'All Day') :
                          selectedEvent.time ? selectedEvent.time : `${selectedEvent.startTime || ''}${selectedEvent.startTime && selectedEvent.endTime ? ' - ' : ''}${selectedEvent.endTime || ''}`}
                      </span>
                    </div>
                  )}
                  
                  {selectedEvent.location && (
                    <div className="flex items-center mb-3">
                      <svg className="h-5 w-5 text-gray-400 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                      <span>{selectedEvent.location}</span>
                    </div>
                  )}

                  {selectedEvent.image && (
                    <div className="mt-4 flex items-center justify-center">
                      <a href={selectedEvent.image} target="_blank" rel="noopener noreferrer" className="block">
                        <img src={selectedEvent.image} alt={selectedEvent.imageAlt || getEventTitle(selectedEvent)} className="w-48 h-48 object-contain border rounded-lg bg-white shadow" />
                      </a>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="bg-gray-50 px-8 py-4 flex justify-end space-x-4">
                <button
                  type="button"
                  className="px-6 py-3 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#630503] transition-colors"
                  onClick={closeModal}
                >
                  Close
                </button>
                {selectedEvent.link && (
                  <a
                    href={selectedEvent.link}
                    className="inline-flex justify-center px-6 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-[#630503] hover:bg-[#4a0402] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#630503] transition-colors"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    More Details
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      <Footer />
    </main>
  );
};

export default Calendar; 